import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ArrowLeft, Download, Loader2, Sparkles, Send, RefreshCw, FileText, Edit3, Check, Save, FileDown, Share2, Play, X, Undo2, Trash2, BoxSelect, Brain, ChevronDown, Paperclip, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { withBackend } from '../utils/api';
import { supabaseHelpers } from '../lib/supabase';
import ShareContentModal from './ShareContentModal';
import { getDefaultModel } from '../utils/models';

const DocumentWorkspace = forwardRef(function DocumentWorkspace({ preloadedData, onBack, onFullscreen, userId, onComplete, onUpdate, existingProjectId, externalChatMode, onChatMessage, chatMessages, onUsageRefresh }, ref) {
  const [phase, setPhase] = useState(preloadedData?.savedHtml ? 'preview' : 'generating');
  const [htmlContent, setHtmlContent] = useState(preloadedData?.savedHtml || '');
  const [title, setTitle] = useState(preloadedData?.savedTitle || '');
  const [editPrompt, setEditPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [editMode, setEditMode] = useState('view'); // view | edit
  const [isSaving, setIsSaving] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [projectId, setProjectId] = useState(existingProjectId || null);
  const [totalPages, setTotalPages] = useState(0);
  // px of content hidden past a fixed page's bottom edge (0 = fits)
  const [clippedPx, setClippedPx] = useState(0);
  const [chatHistory, setChatHistory] = useState([]);
  const [generationStatus, setGenerationStatus] = useState('');
  const [presentMode, setPresentMode] = useState(false);
  const [htmlHistory, setHtmlHistory] = useState([]);
  // Region selection state — lets the user draw a rectangle on the document
  // and scope the next chat-edit to that region. Mirrors HtmlSlideEditor's
  // pattern but adapted to DocumentWorkspace's single-iframe document layout.
  const [regionMode, setRegionMode] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null); // {x, y, width, height} as % of iframe wrapper
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  // Edit-model picker — defaults to the model that created the document so
  // edits inherit the deck's quality bar. User can override per-edit.
  const [editModel, setEditModel] = useState(preloadedData?.flowConfig?.htmlModel || getDefaultModel());
  const [showEditModelMenu, setShowEditModelMenu] = useState(false);
  // Reference images attached to the next chat-edit (mirrors slide chat).
  // Up to 3 base64 data URLs, sent as `reference_images` in the edit payload
  // so the model can use them as visual guidance.
  const [chatImages, setChatImages] = useState([]);
  const iframeWrapperRef = useRef(null);
  const iframeRef = useRef(null);
  const presentIframeRef = useRef(null);
  const editTextareaRef = useRef(null);
  const chatFileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const hasAutoSavedRef = useRef(!!existingProjectId);
  const isSaveUpdateRef = useRef(!!preloadedData?.savedHtml);
  const thumbnailRef = useRef(null);

  const outline = preloadedData?.outline;
  const questionnaireData = preloadedData?.questionnaireData;
  const flowConfig = preloadedData?.flowConfig;

  // Page dimensions match the one-pager template in the backend. Default
  // landscape (1404x992) for one-pagers — matches the editorial-brief
  // reference and fills the page with typical content density. Regular
  // documents stay on A4 portrait (794x1123).
  const docOrientation = (flowConfig?.documentOrientation || 'landscape').toLowerCase();
  const isOnePager = (flowConfig?.documentType === 'one-pager') || (questionnaireData?.documentType === 'one-pager');
  const pageWidth = isOnePager
    ? (docOrientation === 'tabloid' ? 1224 : docOrientation === 'portrait' ? 1100 : 1404)
    : 794;
  const pageMinHeight = isOnePager
    ? (docOrientation === 'tabloid' ? 1584 : docOrientation === 'portrait' ? 1556 : 992)
    : 1123;

  // Auto-generate on mount
  useEffect(() => {
    if (preloadedData?.savedHtml) return;
    if (outline?.slides?.length > 0) {
      setTitle(outline.title || 'Document');
      generateDocument(outline.slides);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onFullscreen?.(true);
    return () => onFullscreen?.(false);
  }, [onFullscreen]);

  const generateDocument = async (sections) => {
    setPhase('generating');
    setGenerationStatus('Generating...');

    thumbnailRef.current = null;

    try {
      const response = await fetch(withBackend('/api/generate-document-html-stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sections,
          title: outline?.title || 'Document',
          document_type: questionnaireData?.documentType || flowConfig?.documentType || 'report',
          document_style: questionnaireData?.documentStyle || flowConfig?.documentStyle || 'corporate',
          audience: questionnaireData?.audience || '',
          html_model: flowConfig?.htmlModel || getDefaultModel(),
          logo_url: flowConfig?.logoUrl || '',
          language: flowConfig?.language || 'auto',
          user_prompt: flowConfig?.prompt || '',
          web_research: flowConfig?.webResearch || '',
          excel_data: flowConfig?.excelData || null,
          file_content: flowConfig?.fileContent || null,
          file_images: flowConfig?.fileImages || null,
          page_count: sections.length,
          special_instructions: flowConfig?.specialInstructions || questionnaireData?.specialInstructions || '',
          content_relationship: flowConfig?.contentRelationship || null,
          brand_identity_id: flowConfig?.brandIdentityId || '',
          user_id: userId || '',
          design_reference_images: flowConfig?.designReferenceImages || [],
          document_orientation: flowConfig?.documentOrientation || 'landscape',
          run_qa: flowConfig?.runQa !== 'no',
          // Round 74: the user's imagery answer never reached documents — a
          // "no imagery" one-pager still got the mandatory hero illustration.
          imagery_mode: questionnaireData?.imageryMode || flowConfig?.imageryMode || 'auto',
        }),
      });

      if (response.status === 403) {
        const err = await response.json().catch(() => ({}));
        if (err.error === 'usage_limit_exceeded') {
          toast.error(err.details?.reason === 'trial_expired'
            ? 'Your 7-day trial has ended. Upgrade to keep creating.'
            : 'You\'ve reached your monthly limit for document generation. Upgrade for more.');
          setPhase('preview');
          return;
        }
      }
      if (!response.ok) throw new Error('Failed to start generation');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            switch (event.type) {
              case 'chunk':
                // Show partial HTML being built in real-time
                setPhase('streaming');
                setHtmlContent(event.text);
                break;
              case 'complete':
                setHtmlContent(event.html);
                setPhase('preview');
                onUsageRefresh?.();
                onChatMessage?.('Document generated! You can now edit it or ask questions about it.');
                break;
              case 'progress':
                if (event.message) onChatMessage?.(event.message);
                break;
              case 'qa_update':
                // One-pager vacancy/overflow QA fixed the page post-complete.
                if (event.html) setHtmlContent(event.html);
                break;
              case 'thumbnail':
                thumbnailRef.current = event.preview_url;
                break;
              case 'error':
                throw new Error(event.error);
              default:
                break;
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('Unexpected token'))
              throw parseErr;
          }
        }
      }
    } catch (error) {
      console.error('Document generation error:', error);
      toast.error(error.message || 'Failed to generate document');
      setPhase('error');
    }
  };

  // Write HTML to iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !htmlContent) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(htmlContent);
    doc.close();

    const resizeIframe = () => {
      try {
        const body = doc.body;
        const html = doc.documentElement;
        if (body && html) {
          // One-pagers are a fixed-size, hard-clipped canvas — pin the iframe to
          // the page height so the preview is a faithful WYSIWYG of the single
          // exported PDF page. Reports grow to show all their A4 pages.
          if (isOnePager) {
            iframe.style.height = pageMinHeight + 'px';
          } else {
            const height = Math.max(
              body.scrollHeight, body.offsetHeight,
              html.clientHeight, html.scrollHeight, html.offsetHeight
            );
            iframe.style.height = height + 'px';
          }
        }
        // Count pages
        const pages = doc.querySelectorAll('.page, [class*="page"]');
        setTotalPages(isOnePager ? 1 : Math.max(1, pages.length));
        // Round 73: content stuffed past a fixed-height page is silently
        // clipped by the page's own overflow:hidden — the user sees an
        // incomplete document with no explanation ("it won't open fully").
        // Measure the clip and surface it.
        let clipped = 0;
        for (const p of pages) {
          if (p.scrollHeight > p.clientHeight + 12) {
            clipped = Math.max(clipped, p.scrollHeight - p.clientHeight);
          }
        }
        setClippedPx(clipped);
      } catch {}
    };

    setTimeout(resizeIframe, 500);
    setTimeout(resizeIframe, 1500);
    setTimeout(resizeIframe, 3000);

    if (editMode === 'edit') {
      setTimeout(() => { enableTextEditing(); enableStylePicker(); }, 600);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlContent, phase]);

  // Undo support (Ctrl+Z)
  const pushToHistory = useCallback((html) => {
    if (!html) return;
    setHtmlHistory(prev => [...prev.slice(-19), html]);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        setHtmlHistory(prev => {
          if (prev.length === 0) return prev;
          const last = prev[prev.length - 1];
          setHtmlContent(last);
          return prev.slice(0, -1);
        });
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && editMode === 'edit') {
        const activeEl = document.activeElement;
        if (activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA') return;
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        const selected = doc.querySelector('[style*="outline: 2px solid"]');
        if (selected && selected !== doc.body && selected !== doc.documentElement) {
          e.preventDefault();
          pushToHistory(htmlContent);
          selected.remove();
          setHtmlContent('<!DOCTYPE html>' + doc.documentElement.outerHTML);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editMode, htmlContent, pushToHistory]);

  // Auto-save debounce after inline edits
  const editSaveTimerRef = useRef(null);
  const scheduleEditAutoSave = useCallback(() => {
    if (editSaveTimerRef.current) clearTimeout(editSaveTimerRef.current);
    editSaveTimerRef.current = setTimeout(() => {
      const iframe = iframeRef.current;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      if (!doc) return;
      // Serialize current state without disabling editing
      const clonedDoc = doc.cloneNode(true);
      clonedDoc.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
      const pickerStyle = clonedDoc.getElementById('style-picker-css');
      if (pickerStyle) pickerStyle.remove();
      clonedDoc.querySelectorAll('*').forEach(el => {
        if (el.style.outline) el.style.removeProperty('outline');
        if (el.style.outlineOffset) el.style.removeProperty('outline-offset');
        if (el.style.cursor === 'text') el.style.removeProperty('cursor');
        if (el.getAttribute('style') === '') el.removeAttribute('style');
      });
      const serialized = '<!DOCTYPE html>' + clonedDoc.documentElement.outerHTML;
      isSaveUpdateRef.current = true;
      setHtmlContent(serialized);
    }, 2000);
  }, []);

  // Text editing
  const enableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const textElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, li, td, th, a, label, figcaption');
    textElements.forEach(el => {
      el.contentEditable = 'true';
      el.style.outline = 'none';
      el.style.cursor = 'text';
      el.addEventListener('focus', () => {
        el.style.outline = '2px solid #3b82f6';
        el.style.outlineOffset = '2px';
        el.style.borderRadius = '2px';
      });
      el.addEventListener('blur', () => {
        el.style.outline = 'none';
        scheduleEditAutoSave();
      });
      el.addEventListener('input', () => {
        scheduleEditAutoSave();
      });
    });
  }, [scheduleEditAutoSave]);

  const disableTextEditing = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const textElements = doc.querySelectorAll('[contenteditable="true"]');
    textElements.forEach(el => {
      el.contentEditable = 'false';
      el.style.outline = 'none';
      el.style.cursor = '';
    });
  }, []);

  // Style picker — click to change color/font-size on elements
  const enableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    // Add a subtle hover effect for style-editable elements
    const style = doc.createElement('style');
    style.id = 'style-picker-css';
    style.textContent = `
      [contenteditable="true"]:hover { outline: 2px dashed #8b5cf6 !important; outline-offset: 2px; }
    `;
    doc.head.appendChild(style);
  }, []);

  const disableStylePicker = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    const style = doc.getElementById('style-picker-css');
    if (style) style.remove();
  }, []);

  // Single edit toggle — enables BOTH text editing and style picker
  const handleEditToggle = () => {
    if (editMode === 'edit') {
      disableTextEditing();
      disableStylePicker();
      serializeAndUpdateHtml();
      setEditMode('view');
    } else {
      pushToHistory(htmlContent);
      setEditMode('edit');
      setTimeout(() => { enableTextEditing(); enableStylePicker(); }, 100);
    }
  };

  const cleanupEditArtifacts = useCallback((doc) => {
    if (!doc) return;
    doc.querySelectorAll('[contenteditable]').forEach(el => {
      el.removeAttribute('contenteditable');
    });
    // Remove style picker CSS
    const pickerStyle = doc.getElementById('style-picker-css');
    if (pickerStyle) pickerStyle.remove();
    // Clean up inline outline/cursor styles from editing
    doc.querySelectorAll('*').forEach(el => {
      if (el.style.outline) el.style.removeProperty('outline');
      if (el.style.outlineOffset) el.style.removeProperty('outline-offset');
      if (el.style.cursor === 'text') el.style.removeProperty('cursor');
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
  }, []);

  const serializeAndUpdateHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;
    cleanupEditArtifacts(doc);
    const serialized = '<!DOCTYPE html>' + doc.documentElement.outerHTML;
    setHtmlContent(serialized);
  };

  const serializeHtml = () => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return htmlContent;
    cleanupEditArtifacts(doc);
    return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
  };

  // Region selection helpers — convert mouse coords on the iframe wrapper to
  // percentage coords (relative to the iframe's bounding rect) so the region
  // is resolution-independent.
  const getRegionPercentCoords = (e) => {
    const wrapper = iframeWrapperRef.current;
    if (!wrapper) return null;
    const rect = wrapper.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  const handleRegionMouseDown = (e) => {
    if (!regionMode) return;
    e.preventDefault();
    const coords = getRegionPercentCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    setDrawStart(coords);
    setDrawCurrent(coords);
    setSelectedRegion(null);
  };

  const handleRegionMouseMove = (e) => {
    if (!isDrawing) return;
    const coords = getRegionPercentCoords(e);
    if (coords) setDrawCurrent(coords);
  };

  const handleRegionMouseUp = () => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }
    setIsDrawing(false);
    const rx = Math.min(drawStart.x, drawCurrent.x);
    const ry = Math.min(drawStart.y, drawCurrent.y);
    const rw = Math.abs(drawCurrent.x - drawStart.x);
    const rh = Math.abs(drawCurrent.y - drawStart.y);
    if (rw > 2 && rh > 2) {
      setSelectedRegion({ x: rx, y: ry, width: rw, height: rh });
    }
    setDrawStart(null);
    setDrawCurrent(null);
    setRegionMode(false);
  };

  const getDrawingStyle = () => {
    if (!drawStart || !drawCurrent) return null;
    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const w = Math.abs(drawCurrent.x - drawStart.x);
    const h = Math.abs(drawCurrent.y - drawStart.y);
    return { left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` };
  };

  // Reference image upload — converts selected files to base64 data URLs and
  // appends to chatImages, capped at 3 to match the backend limit.
  const handleChatFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = Math.max(0, 3 - chatImages.length);
    const accept = files.slice(0, remaining).filter(f => f.type.startsWith('image/'));
    if (!accept.length) {
      if (e.target) e.target.value = '';
      return;
    }
    Promise.all(accept.map(f => new Promise(res => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.readAsDataURL(f);
    }))).then(urls => {
      setChatImages(prev => [...prev, ...urls].slice(0, 3));
    });
    if (e.target) e.target.value = '';
  };

  const removeChatImage = (idx) => {
    setChatImages(prev => prev.filter((_, i) => i !== idx));
  };

  // Expose methods for external chat mode and UnifiedWorkspace toolbar
  useImperativeHandle(ref, () => ({
    sendEdit: (message, options) => handleEditRequest(message, options),
    getChatHistory: () => chatHistory,
    getData: () => ({ html: htmlContent, title }),
    save: () => handleSave(),
    share: () => setShowShareModal(true),
    present: () => setPresentMode(true),
    edit: () => handleEditToggle(),
    // Chat controls exposed to UnifiedWorkspace so it can render the
    // document-specific edit toolbar (region toggle, image attach,
    // edit-model picker) in its own chat input. Mirrors CreatePPTWorkspace's
    // getChatControls / setRegionMode / setEditModel / attachImage interface.
    getChatControls: () => ({
      regionMode,
      editModel,
      hasImages: chatImages.length > 0,
      imageCount: chatImages.length,
      hasActiveRegion: !!selectedRegion,
    }),
    setRegionMode: (v) => {
      if (v) {
        setRegionMode(true);
        setSelectedRegion(null);
        setEditMode('view');
      } else {
        setRegionMode(false);
      }
    },
    clearRegion: () => setSelectedRegion(null),
    setEditModel: (v) => setEditModel(v),
    attachImage: () => chatFileInputRef.current?.click(),
    undo: () => {
      setHtmlHistory(prev => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        setHtmlContent(last);
        return prev.slice(0, -1);
      });
    },
    exportHtml: () => handleDownloadHTML(),
    exportPng: async () => {
      if (!htmlContent) return;
      toast.loading('Generating PNG...', { id: 'png-export' });
      try {
        const response = await fetch(withBackend('/screenshot-html'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html_source: htmlContent, width: isOnePager ? pageWidth : 794, full_page: true }),
        });
        if (!response.ok) throw new Error('Screenshot failed');
        const data = await response.json();
        if (!data.screenshot) throw new Error('No screenshot');
        const link = document.createElement('a');
        link.download = `${title || 'document'}.png`;
        link.href = `data:image/png;base64,${data.screenshot}`;
        link.click();
        toast.success('PNG downloaded!', { id: 'png-export' });
      } catch {
        toast.error('Failed to export PNG', { id: 'png-export' });
      }
    },
    exportPdf: () => handleDownloadPDF(),
    exportDocx: () => handleDownloadDocx(),
  }));

  // AI edit — sends to /edit-html-slide with document_mode
  const handleEditRequest = async (messageOverride, options = {}) => {
    const request = (messageOverride || editPrompt).trim();
    if (!request) return;
    setEditPrompt('');
    setIsRegenerating(true);

    // Capture region snapshot and image attachments now, then clear from UI;
    // the model receives them as part of the request, the visual chips go
    // away once the edit is in flight. Attachments forwarded by the outer
    // chat (UnifiedWorkspace '+' button) arrive via options.referenceImages.
    const regionForEdit = selectedRegion;
    const imagesForEdit = [...chatImages, ...(options.referenceImages || [])];
    setSelectedRegion(null);
    setRegionMode(false);
    setChatImages([]);

    setChatHistory(prev => [...prev, { role: 'user', content: request, timestamp: Date.now(), region: regionForEdit || null, imageCount: imagesForEdit.length }]);

    try {
      const currentHtml = serializeHtml();
      // Augment the edit prompt with region instructions. The backend's
      // /edit-html-slide already accepts a `highlight_region` payload field
      // (used by the slide flow) — we send it here too, plus we hard-encode
      // the constraint into the text prompt so the model sees it inline.
      let editPromptText = request;
      if (regionForEdit) {
        const r = regionForEdit;
        editPromptText = `${request}\n\nIMPORTANT — REGION SCOPE: Apply this edit ONLY to the region of the document at approximately x:${r.x.toFixed(1)}%, y:${r.y.toFixed(1)}%, width:${r.width.toFixed(1)}%, height:${r.height.toFixed(1)}% (relative to the document viewport, top-left origin). Leave content outside this region untouched. If the highlighted region contains a single section/card/heading, edit that element. If it contains a fragment of a larger layout, edit just the visible portion.`;
      }
      // Pass document type + orientation so the backend can apply the right
      // page-size rules (one-pager landscape vs portrait vs multi-page report).
      const docType = flowConfig?.documentType || questionnaireData?.documentType || 'report';
      const docOrient = flowConfig?.documentOrientation || 'portrait';
      const response = await fetch(withBackend('/edit-html-slide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: crypto.randomUUID(),
          html_source: currentHtml,
          edit_prompt: editPromptText,
          document_mode: true,
          document_type: docType,
          document_orientation: docOrient,
          html_model: editModel || flowConfig?.htmlModel || getDefaultModel(),
          brand_identity_id: flowConfig?.brandIdentityId || '',
          highlight_region: regionForEdit || null,
          reference_images: imagesForEdit,
          user_id: userId || '',
          run_qa: flowConfig?.runQa !== 'no',
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Failed to update document');
      }

      const data = await response.json();
      if (data.html_source) {
        setHtmlContent(data.html_source);
        setChatHistory(prev => [...prev, { role: 'bot', content: 'Document updated.', timestamp: Date.now() }]);
        onChatMessage?.('Document updated.');
        toast.success('Document updated!');
      }
    } catch (err) {
      // Surface the backend's explanation (plan limits, model availability)
      // instead of a generic retry hint that may be wrong advice.
      const msg = err?.message || 'Failed to update document';
      toast.error(msg);
      setChatHistory(prev => [...prev, { role: 'bot', content: msg, timestamp: Date.now(), error: true }]);
      onChatMessage?.(msg);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Save
  const handleSave = () => {
    if (!htmlContent) { toast.error('Nothing to save'); return; }
    setIsSaving(true);
    try {
      const serialized = serializeHtml();
      isSaveUpdateRef.current = true;
      setHtmlContent(serialized);
      if (editMode !== 'view') setEditMode('view');
      handleSaveProject();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProject = async () => {
    if (!userId) return;
    const currentHtml = serializeHtml();
    if (!currentHtml) return;

    try {
      const documentData = {
        type: 'document',
        html_content: currentHtml,
        title: title,
        section_count: outline?.slides?.length || 0,
        metadata: {
          documentType: flowConfig?.documentType || questionnaireData?.documentType,
          documentStyle: flowConfig?.documentStyle || questionnaireData?.documentStyle,
          questionnaireData: questionnaireData,
          updated_at: new Date().toISOString(),
          chatHistory: chatMessages || [],
        }
      };

      // Generate thumbnail if not already captured from SSE
      if (!thumbnailRef.current && htmlContent) {
        try {
          const resp = await fetch(withBackend('/screenshot-html'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html_source: htmlContent }),
          });
          if (resp.ok) {
            const result = await resp.json();
            thumbnailRef.current = result.preview_url;
          }
        } catch (e) { console.warn('Thumbnail generation failed:', e); }
      }

      if (projectId) {
        const updatePayload = {
          name: title,
          analysis_data: documentData,
          status: 'completed',
        };
        if (thumbnailRef.current) updatePayload.thumbnail_url = thumbnailRef.current;
        const { error } = await supabaseHelpers.updateProject(projectId, updatePayload);
        if (error) throw error;
        if (onUpdate) onUpdate(projectId, documentData);
        toast.success('Document saved!');
      } else {
        const createPayload = {
          user_id: userId,
          name: title,
          client_name: 'Document',
          status: 'completed',
          slide_count: outline?.slides?.length || 1,
          analysis_data: documentData,
        };
        if (thumbnailRef.current) createPayload.thumbnail_url = thumbnailRef.current;
        const { data, error } = await supabaseHelpers.createProject(createPayload);
        if (error) throw error;
        if (data) {
          setProjectId(data.id);
          hasAutoSavedRef.current = true;
          if (onComplete) onComplete({ projectId: data.id, documentData, preview_url: thumbnailRef.current });
          toast.success('Document saved!');
        }
      }
    } catch (error) {
      console.error('Save project error:', error);
      toast.error('Failed to save project');
    }
  };

  // Autosave after generation completes or content changes
  useEffect(() => {
    if (phase !== 'preview' || !userId) return;
    if (isSaveUpdateRef.current) { isSaveUpdateRef.current = false; return; }
    if (!htmlContent) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { handleSaveProject(); }, 5000);

    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [htmlContent, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Download PDF — use Playwright native PDF rendering for proper page breaks
  const handleDownloadPDF = async () => {
    if (!htmlContent) { toast.error('Document content is not loaded yet — please wait for generation to complete'); return; }
    toast.loading('Generating PDF...', { id: 'pdf-export' });

    try {
      const response = await fetch(withBackend('/export-slides-as-pdf'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slides: [htmlContent],
          presentation_title: title || 'document',
          // Export at the document's TRUE page size. One-pagers are a fixed
          // landscape/portrait/tabloid canvas; ramming them into portrait A4 is
          // what crushed and split them across pages. Reports stay A4 portrait.
          width: pageWidth,
          height: pageMinHeight,
          orientation: isOnePager
            ? (docOrientation === 'portrait' || docOrientation === 'tabloid' ? 'portrait' : 'landscape')
            : 'portrait',
          document_mode: true,
          single_page: isOnePager,
        }),
      });

      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'document'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded!', { id: 'pdf-export' });
    } catch (err) {
      console.error('PDF export error:', err);
      handleDownloadHTML();
      toast.info('PDF export unavailable — HTML downloaded instead', { id: 'pdf-export' });
    }
  };

  const handleDownloadHTML = () => {
    if (!htmlContent) { toast.error('No content to export'); return; }
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${title || 'document'}.html`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded!');
  };

  const handleDownloadDocx = async () => {
    if (!htmlContent) { toast.error('Document content is not loaded yet'); return; }
    toast.loading('Generating Word document...', { id: 'docx-export' });

    try {
      const response = await fetch(withBackend('/api/export-docx'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ html: htmlContent, title: title || 'document' }),
      });

      if (!response.ok) throw new Error('DOCX generation failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'document'}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Word document downloaded!', { id: 'docx-export' });
    } catch (err) {
      console.error('DOCX export error:', err);
      handleDownloadHTML();
      toast.info('Word export unavailable — HTML downloaded instead', { id: 'docx-export' });
    }
  };

  const documentSuggestions = [
    'Make the tone more formal',
    'Add page numbers to all pages',
    'Improve the table formatting',
    'Make headings more prominent',
    'Expand the executive summary',
    'Add a conclusion section',
  ];

  return (
    <div className={`${externalChatMode ? 'h-full' : 'h-screen'} flex flex-col ${externalChatMode ? 'bg-muted/30' : 'bg-background'}`}>
      {/* Header — hidden in externalChatMode (UnifiedWorkspace toolbar handles it) */}
      {!externalChatMode && (
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-card/50 backdrop-blur-sm">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-foreground truncate">{title}</h2>
          <p className="text-xs text-muted-foreground">
            {phase === 'generating' || phase === 'streaming'
              ? 'Generating document...'
              : `${outline?.slides?.length || 0} sections${totalPages > 0 ? ` \u00b7 ${totalPages} page${totalPages !== 1 ? 's' : ''}` : ''}`}
          </p>
        </div>
        {phase === 'preview' && (
          <div className="flex items-center gap-2">
            {/* Select Region — lets user draw a rectangle on the document and
                scope the next chat-edit to that region. Mirrors the slide
                editor's Select Region behaviour. */}
            <Button
              size="sm"
              variant={regionMode ? 'default' : 'outline'}
              className={regionMode ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}
              onClick={() => {
                if (regionMode) {
                  setRegionMode(false);
                } else {
                  setRegionMode(true);
                  setSelectedRegion(null);
                  setEditMode('view'); // region drawing requires view mode
                }
              }}
              title="Draw a region — your next chat-edit will only modify that area"
            >
              <BoxSelect className="w-3.5 h-3.5 mr-1.5" />
              {regionMode ? 'Cancel' : 'Select Region'}
            </Button>
            <Button
              size="sm"
              variant={editMode === 'edit' ? 'default' : 'outline'}
              className={editMode === 'edit' ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
              onClick={handleEditToggle}
            >
              {editMode === 'edit' ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Done</> : <><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit</>}
            </Button>
            {editMode === 'edit' && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => {
                  const doc = iframeRef.current?.contentDocument;
                  if (!doc) return;
                  const selected = doc.querySelector('[style*="outline: 2px solid"]');
                  if (selected && selected !== doc.body && selected !== doc.documentElement) {
                    pushToHistory(htmlContent);
                    selected.remove();
                    serializeAndUpdateHtml();
                  }
                }}
                title="Delete selected element (Del)"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
            {htmlHistory.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => {
                if (htmlHistory.length === 0) return;
                const last = htmlHistory[htmlHistory.length - 1];
                setHtmlHistory(prev => prev.slice(0, -1));
                setHtmlContent(last);
              }}>
                <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleSave} disabled={isSaving}>
              <Save className={`w-3.5 h-3.5 mr-1.5 ${isSaving ? 'animate-spin' : ''}`} />
              Save
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)}>
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPresentMode(true)}>
              <Play className="w-3.5 h-3.5 mr-1.5" /> Present
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadHTML}>
              <Download className="w-3.5 h-3.5 mr-1.5" />
              HTML
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadDocx}>
              <FileText className="w-3.5 h-3.5 mr-1.5" />
              Word
            </Button>
            <Button size="sm" onClick={handleDownloadPDF} className="bg-gradient-to-r from-blue-500 to-sky-500 text-white hover:from-blue-600 hover:to-sky-600">
              <FileDown className="w-3.5 h-3.5 mr-1.5" />
              PDF
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Edit mode indicator */}
      {editMode === 'edit' && (
        <div className="px-4 py-1.5 text-xs text-white text-center bg-blue-500">
          Click text to edit \u00b7 Click elements to select \u00b7 Del to delete \u00b7 Ctrl+Z to undo
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Document Preview */}
        <div className="flex-1 overflow-y-auto bg-slate-100 dark:bg-slate-900">
          {phase === 'generating' && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="relative">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                <FileText className="w-5 h-5 text-blue-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="text-sm font-medium text-foreground mt-4">{generationStatus || 'Generating your document...'}</p>
            </div>
          )}

          {phase === 'streaming' && htmlContent && (
            <div className="flex justify-center py-6 relative">
              <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10 bg-blue-500/90 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg backdrop-blur-sm">
                <Loader2 className="w-3 h-3 animate-spin" /> Generating...
              </div>
              <div className="rounded-lg shadow-xl overflow-hidden" style={{ width: pageWidth }}>
                <iframe
                  ref={iframeRef}
                  title="Document Preview"
                  style={{
                    width: pageWidth,
                    minHeight: pageMinHeight,
                    border: 'none',
                    display: 'block',
                  }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-sm text-red-500 mb-3">Failed to generate document</p>
              <Button variant="outline" onClick={() => generateDocument(outline?.slides || [])}>
                <RefreshCw className="w-4 h-4 mr-2" /> Try Again
              </Button>
            </div>
          )}

          {phase === 'preview' && htmlContent && (
            <div className="flex flex-col items-center py-6">
              {clippedPx > 12 && (
                <div className="mb-3 max-w-[720px] rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-2.5 text-[13px] text-amber-900 dark:text-amber-200">
                  <b>Some content doesn&apos;t fit the page</b> — about {Math.round(clippedPx)}px is hidden below the
                  page edge. Ask the AI to condense it (e.g. &quot;tighten the content to fit on one page&quot;)
                  or switch this document to a multi-page format.
                </div>
              )}
              <div
                ref={iframeWrapperRef}
                className="rounded-lg shadow-xl overflow-hidden relative"
                style={{ width: pageWidth }}
              >
                <iframe
                  ref={iframeRef}
                  title="Document Preview"
                  style={{
                    width: pageWidth,
                    minHeight: pageMinHeight,
                    border: 'none',
                    display: 'block',
                  }}
                  sandbox="allow-scripts allow-same-origin"
                />
                {/* Region drawing overlay — active when user clicks Select Region */}
                {regionMode && (
                  <div
                    className="absolute inset-0 z-10"
                    style={{ cursor: 'crosshair', backgroundColor: 'rgba(245, 158, 11, 0.04)' }}
                    onMouseDown={handleRegionMouseDown}
                    onMouseMove={handleRegionMouseMove}
                    onMouseUp={handleRegionMouseUp}
                    onMouseLeave={() => { if (isDrawing) handleRegionMouseUp(); }}
                  >
                    <div className="absolute top-3 left-3 z-20 px-2 py-1 bg-amber-500 text-white text-xs rounded-md shadow-md pointer-events-none">
                      Drag to select a region for the next edit
                    </div>
                    {isDrawing && getDrawingStyle() && (
                      <div
                        className="absolute border-2 border-amber-500 bg-amber-500/20 rounded pointer-events-none"
                        style={getDrawingStyle()}
                      />
                    )}
                  </div>
                )}
                {/* Selected region highlight — persists until edit submits or user clears */}
                {selectedRegion && !regionMode && (
                  <div
                    className="absolute z-10 border-2 border-amber-500 bg-amber-500/10 rounded pointer-events-none"
                    style={{
                      left: `${selectedRegion.x}%`,
                      top: `${selectedRegion.y}%`,
                      width: `${selectedRegion.width}%`,
                      height: `${selectedRegion.height}%`,
                    }}
                  >
                    <button
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center pointer-events-auto hover:bg-red-600 shadow"
                      onClick={() => setSelectedRegion(null)}
                      title="Clear region"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel — Edit with AI (hidden in external chat mode) */}
        {phase === 'preview' && !externalChatMode && (
          <div className="w-[320px] border-l border-border/50 flex flex-col bg-card/50">
            <div className="px-4 py-3 border-b border-border/50">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500" />
                Edit with AI
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {chatHistory.length === 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-3">Quick suggestions:</p>
                  {documentSuggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setEditPrompt(suggestion)}
                      className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-border/50 hover:border-blue-400/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-muted-foreground"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {chatHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] text-xs px-3 py-2 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-blue-500 text-white'
                          : msg.error
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'bg-muted text-foreground'
                      }`}>
                        {msg.role === 'bot' && <Sparkles className="w-3 h-3 inline mr-1 opacity-60" />}
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {isRegenerating && (
                    <div className="flex justify-start">
                      <div className="bg-muted text-foreground text-xs px-3 py-2 rounded-lg flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Updating...
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Describe what to change</p>
              {/* Active context chips: image attachments + active region.
                  Mirrors the slide-chat pattern so users see what's about to
                  be sent with their next edit. */}
              {(chatImages.length > 0 || selectedRegion) && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {chatImages.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-10 h-10 rounded-md object-cover border border-border/60" />
                      <button
                        onClick={() => removeChatImage(i)}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-800 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                  {selectedRegion && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[11px] font-medium">
                      <BoxSelect className="w-3 h-3" /> Region scoped
                      <button
                        onClick={() => setSelectedRegion(null)}
                        className="hover:bg-amber-200 dark:hover:bg-amber-900/50 rounded p-0.5 -mr-0.5"
                        title="Clear region"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  ref={editTextareaRef}
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleEditRequest();
                    }
                  }}
                  placeholder="e.g., Add more detail to section 2..."
                  rows={1}
                  className="flex-1 text-sm bg-transparent border border-border/50 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400/50"
                />
                <button
                  onClick={handleEditRequest}
                  disabled={!editPrompt.trim() || isRegenerating}
                  className="w-9 h-9 rounded-lg bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              {/* Hidden file input for image attachments */}
              <input
                ref={chatFileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                onChange={handleChatFilesSelected}
                className="hidden"
              />
              {/* Bottom toolbar: paperclip + region toggle + model picker.
                  The region toggle also lives in the standalone header, but
                  that header is hidden when DocumentWorkspace is embedded in
                  UnifiedWorkspace (externalChatMode). Mirroring it here makes
                  it always reachable. */}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => chatFileInputRef.current?.click()}
                    disabled={chatImages.length >= 3}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title={chatImages.length >= 3 ? 'Maximum 3 reference images per edit' : 'Attach a reference image (max 3)'}
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {chatImages.length > 0 && <span>{chatImages.length}/3</span>}
                  </button>
                  <button
                    onClick={() => {
                      if (regionMode) {
                        setRegionMode(false);
                      } else {
                        setRegionMode(true);
                        setSelectedRegion(null);
                        setEditMode('view');
                      }
                    }}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      regionMode
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                        : 'text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                    title="Draw a region — your next edit will only modify that area"
                  >
                    <BoxSelect className="w-3.5 h-3.5" />
                    {regionMode ? 'Cancel' : 'Region'}
                  </button>
                </div>
                {(() => {
                  const EDIT_MODELS = [
                    { value: 'flash', label: 'Flash', desc: 'Fast' },
                    { value: 'pro', label: 'Gemini 3.1 Pro', desc: 'Vision' },
                    { value: 'sonnet', label: 'Sonnet 5', desc: 'Balanced' },
                    { value: 'kimi', label: 'Kimi K3', desc: 'Moonshot' },
                    { value: 'opus', label: 'Opus 5', desc: 'Best' },
                    { value: 'sol', label: 'GPT-5.6 Sol', desc: 'Premium' },
                    { value: 'astra', label: 'GPT-6 Astra', desc: 'Ultra' },
                  ];
                  const current = EDIT_MODELS.find(m => m.value === editModel) || EDIT_MODELS[0];
                  return (
                    <div className="relative">
                      <button
                        onClick={() => setShowEditModelMenu(v => !v)}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        title="Pick the model that performs this edit. Defaults to the model that created the document."
                      >
                        <Brain className="w-3.5 h-3.5" />
                        {current.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {showEditModelMenu && (
                        <div className="absolute bottom-full right-0 mb-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px] z-50">
                          {EDIT_MODELS.map(m => (
                            <button
                              key={m.value}
                              onClick={() => { setEditModel(m.value); setShowEditModelMenu(false); }}
                              className={`w-full px-3 py-1.5 text-left text-xs hover:bg-muted/50 flex items-center justify-between gap-2 ${
                                editModel === m.value ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-900 dark:text-violet-200' : ''
                              }`}
                            >
                              <span className="font-medium">{m.label}</span>
                              <span className="text-muted-foreground">{m.desc}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Present mode overlay */}
      {presentMode && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-white">
            <span className="text-sm font-medium">{title}</span>
            <button onClick={() => setPresentMode(false)} className="p-1 hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-auto bg-slate-100 flex justify-center py-6">
            <div style={{ width: pageWidth }}>
              <iframe
                ref={presentIframeRef}
                title="Document Present"
                srcDoc={htmlContent}
                style={{ width: pageWidth, minHeight: '100vh', border: 'none', display: 'block' }}
                sandbox="allow-scripts allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
      <ShareContentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        contentType="document"
        title={title || 'Document'}
        userId={userId}
        htmlContent={htmlContent}
      />
    </div>
  );
});

export default DocumentWorkspace;
